import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 5000;

/**
 * PhoenixFlareBanner — pinned, animated burning header that takes over the
 * top of the page when a player ignites a Phoenix Flare Quill. The banner
 * burns for the flare's TTL (30s / 90s) then evaporates. Visible to all
 * logged-in players (global scope).
 *
 * Triggers:
 *   • Server poll every 5s: GET /api/chat/flare/active
 *   • Same-tab: listens for `omni:phoenix-flare-fired` window event
 */
export default function PhoenixFlareBanner() {
  const { isAuthed } = useAuth();
  const isAuthenticated = isAuthed;
  const [flare, setFlare] = useState(null);
  const [now, setNow] = useState(() => Date.now());

  // Poll for active flare
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;

    async function check() {
      try {
        const { data } = await axios.get(`${API}/chat/flare/active`);
        if (cancelled) return;
        const f = data?.flare;
        if (!f) return setFlare(null);
        setFlare((prev) =>
          !prev || (prev.flare_id !== f.flare_id && new Date(f.starts_at) > new Date(prev.starts_at))
            ? f
            : prev
        );
      } catch (e) {
        /* ignore */
      }
    }
    check();
    const iv = setInterval(check, POLL_MS);
    return () => { cancelled = true; clearInterval(iv); };
  }, [isAuthenticated]);

  // Local event trigger (immediate response when sender fires their own flare)
  useEffect(() => {
    function onFire(e) {
      const f = e.detail;
      if (!f || !f.flare_id) return;
      setFlare(f);
    }
    window.addEventListener("omni:phoenix-flare-fired", onFire);
    return () => window.removeEventListener("omni:phoenix-flare-fired", onFire);
  }, []);

  // 1s ticker to compute countdown / expiry
  useEffect(() => {
    if (!flare) return;
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, [flare]);

  if (!isAuthenticated || !flare) return null;
  const endsAt = new Date(flare.ends_at).getTime();
  const startsAt = new Date(flare.starts_at).getTime();
  if (Number.isNaN(endsAt) || endsAt <= now) return null;

  const totalMs = endsAt - startsAt;
  const remainingMs = endsAt - now;
  const remainingS = Math.max(0, Math.ceil(remainingMs / 1000));
  const isLong = flare.tier === "large";

  return (
    <>
      <style>{`
        @keyframes phoenix-flare-flicker {
          0%, 100% { filter: brightness(1) drop-shadow(0 0 12px #f59e0b); }
          25%      { filter: brightness(1.15) drop-shadow(0 0 18px #fb7185); }
          50%      { filter: brightness(0.95) drop-shadow(0 0 14px #fbbf24); }
          75%      { filter: brightness(1.2) drop-shadow(0 0 20px #f97316); }
        }
        @keyframes phoenix-flare-scroll-bg {
          0%   { background-position:   0% 50%; }
          100% { background-position: 100% 50%; }
        }
        @keyframes phoenix-flare-ember {
          0%   { transform: translateY(0) scale(1); opacity: 0; }
          15%  { opacity: 0.9; }
          100% { transform: translateY(-40px) scale(0.4); opacity: 0; }
        }
        @keyframes phoenix-flare-text-shimmer {
          0%, 100% { text-shadow: 0 0 8px #fff, 0 0 16px #fbbf24, 0 0 24px #f97316, 0 0 40px #b91c1c; }
          50%      { text-shadow: 0 0 12px #fff, 0 0 22px #fde047, 0 0 32px #ef4444, 0 0 50px #7c2d12; }
        }
        .phoenix-flare-banner {
          background: linear-gradient(
            90deg,
            #1a0500 0%,
            #b91c1c 20%,
            #f59e0b 40%,
            #fde047 50%,
            #f59e0b 60%,
            #b91c1c 80%,
            #1a0500 100%
          );
          background-size: 300% 100%;
          animation:
            phoenix-flare-scroll-bg 6s linear infinite,
            phoenix-flare-flicker 1.6s ease-in-out infinite;
        }
        .phoenix-flare-ember {
          position: absolute;
          bottom: -4px;
          width: 4px;
          height: 4px;
          background: #fde047;
          border-radius: 50%;
          box-shadow: 0 0 8px #fbbf24, 0 0 16px #f97316;
          animation: phoenix-flare-ember 2.2s ease-out infinite;
        }
        .phoenix-flare-text {
          animation: phoenix-flare-text-shimmer 2s ease-in-out infinite;
        }
      `}</style>
      <div
        data-testid="phoenix-flare-banner"
        className="phoenix-flare-banner fixed top-0 left-0 right-0 z-[120] border-b-2 border-yellow-300 overflow-hidden"
        style={{ boxShadow: "0 4px 30px rgba(251, 146, 60, 0.7)" }}
      >
        {/* Embers */}
        {Array.from({ length: 18 }).map((_, i) => (
          <span
            key={i}
            className="phoenix-flare-ember"
            style={{
              left: `${(i * 5.5) % 100}%`,
              animationDelay: `${(i * 0.13) % 2.2}s`,
              animationDuration: `${1.6 + (i % 4) * 0.25}s`,
            }}
          />
        ))}

        <div className="relative max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
          <span
            aria-hidden
            className="text-2xl md:text-3xl"
            style={{ filter: "drop-shadow(0 0 8px #fde047)" }}
          >
            🔥
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-black uppercase tracking-[0.35em] text-yellow-200/90 leading-none">
              Phoenix Flame Flare {isLong ? "· Eternal" : "· Spark"}
            </div>
            <div
              className="phoenix-flare-text font-black uppercase tracking-wider text-white text-base md:text-2xl leading-tight truncate"
              data-testid="phoenix-flare-text"
            >
              {flare.text}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-yellow-100/80 leading-none mt-0.5">
              — {flare.sender_name || "An athlete"}
              {flare.sender_level ? <span className="opacity-70"> · Lv {flare.sender_level}</span> : null}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[9px] uppercase tracking-widest text-yellow-100/80">Burns for</div>
            <div data-testid="phoenix-flare-countdown" className="text-yellow-100 font-black text-xl tabular-nums">
              {remainingS}s
            </div>
            {/* progress sliver */}
            <div className="mt-1 w-20 h-1 bg-black/40 overflow-hidden" style={{ borderRadius: 2 }}>
              <div
                className="h-full bg-yellow-300"
                style={{ width: `${(remainingMs / totalMs) * 100}%` }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
